import { Table, StatusPill } from '../../../design-system';
import { useReport, ReportCard, BarList, Donut, AreaTrend, CountPills, fmtNum, fmtPct } from './reportsKit';

const statusTone = { active: 'success', onboarding: 'info', inactive: 'warning', closed: 'neutral' };

export default function ClientReports({ range }) {
  const portfolio = useReport('client-portfolio');
  const trend = useReport('client-trend', range);
  const adoption = useReport('service-adoption');

  const st = portfolio.data?.byStatus || {};
  const statusDonut = [
    { label: 'Active', value: st.active || 0, color: 'var(--ds-success-600)' },
    { label: 'Onboarding', value: st.onboarding || 0, color: 'var(--ds-info-600)' },
    { label: 'Inactive', value: st.inactive || 0, color: 'var(--ds-warning-600)' },
    { label: 'Closed', value: st.closed || 0, color: 'var(--ds-neutral-400)' },
  ];

  return (
    <div className="rep-stack">
      {/* D1/D2 — Portfolio + Partner Book */}
      <ReportCard icon="🗂️" title="Client Portfolio" subtitle="Book by status, type and owning partner" loading={portfolio.loading} error={portfolio.error}>
        <div className="rep-grid-3">
          <Donut segments={statusDonut} centerValue={fmtNum(portfolio.data?.total)} centerLabel="clients" />
          <div>
            <p className="rep-note">Book size by partner</p>
            <BarList items={Object.entries(portfolio.data?.byPartner || {}).map(([label, value]) => ({ label, value }))} color="var(--ds-accent-600)" />
            <p className="rep-note">By type</p>
            <CountPills counts={portfolio.data?.byType} />
          </div>
        </div>
        <p className="rep-note">All clients</p>
        <Table
          keyField="id"
          columns={[
            { key: 'name', header: 'Client', render: (r) => <strong>{r.name}</strong> },
            { key: 'status', header: 'Status', render: (r) => <StatusPill variant={statusTone[r.status] || 'neutral'} label={r.status} /> },
            { key: 'clientType', header: 'Type' },
            { key: 'partner', header: 'Partner' },
            { key: 'manager', header: 'Manager' },
            { key: 'gstinCount', header: 'GSTINs' },
          ]}
          data={portfolio.data?.rows || []}
        />
      </ReportCard>

      {/* D3 — New vs Lost */}
      <ReportCard icon="📈" title="New Clients Onboarded" subtitle="Onboarding trend by month" loading={trend.loading} error={trend.error}>
        <AreaTrend data={(trend.data?.newByMonth || []).map((m) => ({ key: m.key, value: m.count }))} valueFmt={fmtNum} color="var(--ds-success-600)" />
        <p className="rep-note">Current status mix</p>
        <CountPills counts={trend.data?.statusSnapshot} tones={{ active: 'green', onboarding: 'blue', inactive: 'amber', closed: 'red' }} />
        {trend.data?.note && <p className="rep-note-plain">{trend.data.note}</p>}
      </ReportCard>

      {/* D4 — Service Adoption */}
      <ReportCard icon="🧩" title="Service Adoption" subtitle="Clients enrolled in each compliance head — low bars are cross-sell gaps" loading={adoption.loading} error={adoption.error}>
        <BarList items={(adoption.data?.rows || []).map((r) => ({ label: r.name, value: r.adoptionPct }))} valueFmt={fmtPct} color="var(--ds-info-600)" max={100} emptyText="No enrollments" />
        <p className="rep-note">Detail</p>
        <Table
          keyField="code"
          columns={[
            { key: 'name', header: 'Head', render: (r) => <strong>{r.name}</strong> },
            { key: 'category', header: 'Category' },
            { key: 'clientCount', header: 'Clients', render: (r) => fmtNum(r.clientCount) },
            { key: 'adoptionPct', header: 'Adoption', render: (r) => fmtPct(r.adoptionPct) },
          ]}
          data={adoption.data?.rows || []}
        />
      </ReportCard>
    </div>
  );
}
